/**
 * Theme store (zustand). Holds the light/dark choice, persists it to localStorage
 * under "mobui-theme", and reflects it onto <html class="dark"> so the Tailwind
 * `darkMode: ["class"]` tokens (src/index.css .dark {}) take effect.
 *
 * main.tsx calls initTheme() before the first render so there is no light flash.
 */

import { create } from "zustand";

export type Theme = "light" | "dark";

const STORAGE_KEY = "mobui-theme";

/**
 * localStorage access is wrapped: in private mode / with third-party storage
 * blocked, `localStorage` getters can throw SecurityError. An unguarded access
 * here (runs before the first render) would crash the whole app (F21).
 */
function safeGetItem(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable — theme simply won't persist across reloads */
  }
}

/** Read the persisted theme, falling back to the OS preference, then light. */
function readStoredTheme(): Theme {
  if (typeof window === "undefined") return "light";
  const stored = safeGetItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/** Reflect the theme onto <html> without persisting (used at boot). */
function reflectTheme(theme: Theme): void {
  if (typeof document !== "undefined") {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }
}

/** Reflect the theme onto <html> AND persist it (explicit user choice). */
function applyTheme(theme: Theme): void {
  reflectTheme(theme);
  if (typeof window !== "undefined") {
    safeSetItem(STORAGE_KEY, theme);
  }
}

interface ThemeState {
  theme: Theme;
  toggle: () => void;
  set: (theme: Theme) => void;
}

export const useTheme = create<ThemeState>((set, get) => ({
  theme: readStoredTheme(),
  toggle: () => {
    const next: Theme = get().theme === "dark" ? "light" : "dark";
    applyTheme(next);
    set({ theme: next });
  },
  set: (theme) => {
    applyTheme(theme);
    set({ theme });
  },
}));

/**
 * Apply the stored theme to <html> at boot. Call once from main.tsx before render
 * so the first paint already matches the persisted choice (no flash of light).
 * Only reflects onto <html> — does NOT persist, so an unset choice keeps tracking
 * the OS preference instead of being frozen into localStorage on first load.
 */
export function initTheme(): void {
  reflectTheme(useTheme.getState().theme);
}
